import { getCountries, getCountryCallingCode } from '@/utils/phone-utils';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(enLocale);

export interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  priority?: number;
}

const getCountryFlag = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => {
      const codePoint = char.codePointAt(0);
      return codePoint ? 127397 + codePoint : 0;
    });
  return String.fromCodePoint(...codePoints);
};

const PRIORITY_COUNTRIES: Record<string, number> = {
  GB: 1,
  US: 2,
};

const generateCountries = (): Country[] => {
  const allCountryCodes = getCountries();

  return allCountryCodes.map(code => {
    const dialCode = `+${getCountryCallingCode(code)}`;
    const name = countries.getName(code, 'en') || code;
    const flag = getCountryFlag(code);
    const priority = PRIORITY_COUNTRIES[code];

    return {
      code,
      name,
      dialCode,
      flag,
      ...(priority && { priority }),
    };
  });
};

export const COUNTRIES: Country[] = generateCountries();

export const getCountriesSorted = (): Country[] => {
  return [...COUNTRIES].sort((a, b) => {
    if (a.priority && b.priority) {
      return a.priority - b.priority;
    }
    if (a.priority && !b.priority) {
      return -1;
    }
    if (!a.priority && b.priority) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });
};

export const getDefaultCountry = (): Country => {
  return COUNTRIES.find(c => c.code === 'GB') || COUNTRIES[0];
};

export const findCountryByCode = (code: string): Country | undefined => {
  return COUNTRIES.find(c => c.code === code);
};

export const findCountryByDialCode = (
  dialCode: string
): Country | undefined => {
  return COUNTRIES.find(c => c.dialCode === dialCode);
};
